import apiClient from '../api/client';

export interface Prompt {
  id: string;
  prompt_type: string;
  prompt_name: string;
  prompt_text: string;
  is_active: boolean;
  version: number;
  search_method: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  metadata?: Record<string, any>;
}

export interface HarmonySettings {
  prompt_id: string;
  chunk_size: number;
  chunk_overlap: number;
  extraction_model: string;
  document_type: string;
}

class PromptService {
  /**
   * Get all prompts, optionally filtered by type and active status
   */
  async getPrompts(promptType?: string, activeOnly?: boolean): Promise<Prompt[]> {
    try {
      const params: any = {};
      if (promptType) params.type = promptType;
      if (activeOnly !== undefined) params.active_only = activeOnly;

      // Use API Gateway URL - it will route to administration service
      const response = await apiClient.get('/prompts', { params });

      // Check if response is wrapped in data object
      const prompts = response.data?.data || response.data || response || [];
      return Array.isArray(prompts) ? prompts : [];
    } catch (error: any) {
      console.error('Error fetching prompts:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to fetch prompts'
      );
    }
  }

  /**
   * Get prompts specifically for Harmony (active harmony prompts only)
   */
  async getHarmonyPrompts(): Promise<Prompt[]> {
    return this.getPrompts('harmony', true);
  }

  /**
   * Get prompts specifically for Symphony (active symphony prompts only)
   */
  async getSymphonyPrompts(): Promise<Prompt[]> {
    return this.getPrompts('symphony', true);
  }

  /**
   * Get current Harmony settings from the document service
   */
  async getHarmonySettings(): Promise<HarmonySettings> {
    try {
      // Use API Gateway URL - it will route to document service
      const response = await apiClient.get('/harmony/settings');
      // Backend wraps response in { data: settings, meta: {...} }
      return response.data?.data || response.data || response;
    } catch (error: any) {
      console.error('Error fetching harmony settings:', error);
      // Return defaults if settings don't exist
      return {
        prompt_id: 'default',
        chunk_size: 2000,
        chunk_overlap: 200,
        extraction_model: 'claude-sonnet-4-5-20250929',
        document_type: 'general'
      };
    }
  }

  /**
   * Update Harmony settings in the document service
   */
  async updateHarmonySettings(settings: HarmonySettings): Promise<HarmonySettings> {
    try {
      // Use API Gateway URL - it will route to document service
      const response = await apiClient.put('/harmony/settings', settings);
      // Backend wraps response in { data: settings, meta: {...} }
      return response.data?.data || response.data || response;
    } catch (error: any) {
      console.error('Error updating harmony settings:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to update harmony settings'
      );
    }
  }

  /**
   * Get current Symphony settings from the document service
   */
  async getSymphonySettings(): Promise<HarmonySettings> {
    try {
      // Use API Gateway URL - it will route to document service
      const response = await apiClient.get('/symphony/settings');
      // Backend wraps response in { data: settings, meta: {...} }
      return response.data?.data || response.data || response;
    } catch (error: any) {
      console.error('Error fetching symphony settings:', error);
      // Return defaults if settings don't exist
      return {
        prompt_id: 'default',
        chunk_size: 2000,
        chunk_overlap: 200,
        extraction_model: 'claude-sonnet-4-5-20250929',
        document_type: 'general'
      };
    }
  }

  /**
   * Update Symphony settings in the document service
   */
  async updateSymphonySettings(settings: HarmonySettings): Promise<HarmonySettings> {
    try {
      // Use API Gateway URL - it will route to document service
      const response = await apiClient.put('/symphony/settings', settings);
      // Backend wraps response in { data: settings, meta: {...} }
      return response.data?.data || response.data || response;
    } catch (error: any) {
      console.error('Error updating symphony settings:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to update symphony settings'
      );
    }
  }

  /**
   * Get available extraction models
   */
  getAvailableModels(): string[] {
    return [
      'claude-sonnet-4-5-20250929',  // Sonnet 4.5 - Default, balanced performance
      'claude-haiku-4-5-20251001',   // Haiku 4.5 - Fast and efficient
      'claude-opus-4-1-20250805',    // Opus 4.1 - Most capable
    ];
  }

  /**
   * Get available document types
   */
  getDocumentTypes(): string[] {
    return [
      'general',
      'technical',
      'legal',
      'medical',
      'financial',
      'research',
      'news',
      'report'
    ];
  }
}

export default new PromptService();